import Decimal from 'decimal.js';
import { RotationCallStatus } from 'src/types/ViewModel/Stowage/Rotation/RotationCallStatus';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { notStowedWeightInTanks, stowedWeightInTanks } from './stowageOperationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { UUID } from 'crypto';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { isPortCallAfterFirstVoyageOrcaWebStowageImosSync } from '../stowageRotationUtils';
import { calculateUnloadingDifference } from './operationDisplayUtil';

export const calculateRotationCallStatuses = (stowagePlan: StowagePlan, firstVoyageOrcaWebStowageImosSync: number): RotationCallStatus[] => {
    if (!stowagePlan.berthCalls) {
        return [];
    }

    return (
        stowagePlan.rotation
            // TODO: NULL-SAFETY - Missing null/undefined check for stowagePlan.rotation before calling .filter()
            .filter((pc) => isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, firstVoyageOrcaWebStowageImosSync))
            .flatMap((pc) =>
                pc.berthCalls?.flatMap((bc) => {
                    return {
                        portCallGuid: bc.portCall.portCallGuid,
                        berthCallGuid: bc.berthCallGuid,
                        numberOfCargoOperations: bc.cargoOperations.length,
                        numberOfCargoOperationsWithMissingStowage: getNumberOfIncompleteCargoOperations(stowagePlan, bc),
                    } as RotationCallStatus;
                })
            ) ?? []
    );
};

export const getRotationCallStatusInBerthCall = (rotationCallStatuses: RotationCallStatus[], berthCallGuid: UUID): RotationCallStatus => {
    // TODO: RETURN-TYPE - Function can return undefined but return type declares RotationCallStatus (should be RotationCallStatus | undefined)
    return rotationCallStatuses?.find((status) => status.berthCallGuid === berthCallGuid);
};

export const getRotationCallStatusInPortCall = (rotationCallStatuses: RotationCallStatus[], portCallGuid: UUID): RotationCallStatus => {
    return rotationCallStatuses
        ?.filter((status) => status.portCallGuid === portCallGuid)
        ?.reduce(
            (acc, status) => {
                acc.numberOfCargoOperations += status.numberOfCargoOperations;
                acc.numberOfCargoOperationsWithMissingStowage += status.numberOfCargoOperationsWithMissingStowage;
                return acc;
            },
            { portCallGuid, berthCallGuid: null, numberOfCargoOperations: 0, numberOfCargoOperationsWithMissingStowage: 0 }
        );
};

export const hasZeroStowedWeight = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): boolean => {
    const stowedWeight = stowedWeightInTanks(stowagePlan, cargoOperation).toDecimalPlaces(3, Decimal.ROUND_DOWN).abs();
    return stowedWeight.eq(0);
};

export const getNumberOfIncompleteCargoOperations = (stowagePlan: StowagePlan, berthCallItem: RotationBerthCall): number => {
    if (stowagePlan?.rotation && berthCallItem) {
        const numberOfOperations = berthCallItem.cargoOperations.reduce((count, op) => {
            // Skip Transit operations - they should not be counted for missing stowage
            if (op.operationType === 'Transit') {
                return count;
            }

            // Check for zero stowed weight (missing stowage)
            if (hasZeroStowedWeight(stowagePlan, op)) {
                return count + 1;
            }

            // Check for invalid stowage in loading operations (negative not-stowed weight)
            if (op.operationType === 'Loading' && notStowedWeightInTanks(stowagePlan, op).lt(0)) {
                return count + 1;
            }

            // Check for unloading operations with remaining cargo to unload (difference between stowed and unstowed)
            if (op.operationType === 'Unloading') {
                const difference = calculateUnloadingDifference(stowagePlan, op);
                if (difference.gt(0)) {
                    return count + 1;
                }
            }

            return count;
        }, 0);
        return numberOfOperations;
    }
    return 0;
};

export const getNumberOfIncompleteStowages = (rotationCallStatuses: RotationCallStatus[]): number => {
    return rotationCallStatuses?.reduce((acc, status) => acc + status.numberOfCargoOperationsWithMissingStowage, 0);
};

// TODO: LOGIC-ERROR - Function name has typo 'Cargooperations' should be 'CargoOperations' (missing capital O). Also this function duplicates getNumberOfIncompleteStowages functionality.
export const getNumberOfIncompleteCargooperations = (rotationCallStatuses: RotationCallStatus[]): number => {
    return rotationCallStatuses?.reduce((acc, status) => acc + status.numberOfCargoOperationsWithMissingStowage, 0);
};

export const getValueOrMax = (value?: number): string => {
    if (value > 99) {
        return '99+';
    }
    // TODO: RETURN-TYPE - Function returns undefined when value is 0, null, or undefined but return type is string (should be string | undefined)
    return value?.toString();
};
